import { MOBILE_APP_ANDROID_PACKAGE, MOBILE_APP_STORE_URLS } from '../constants/mobileApps';
import type { MobilePlatform } from '../utils/mobilePlatform';

/**
 * Link building is kept pure — the caller performs the navigation — so the
 * exact URLs stay unit-testable without stubbing `window.location`.
 */
export class MobileAppRedirectService {
  readonly #storeUrls = MOBILE_APP_STORE_URLS;

  readonly #androidPackage = MOBILE_APP_ANDROID_PACKAGE;

  readonly #scheme = 'teamhub';

  readonly #browserChoiceKeyPrefix = 'mobileJoinInBrowser:';

  getStoreUrl(platform: MobilePlatform): string {
    return this.#storeUrls[platform];
  }

  /**
   * Android gets an `intent://` URL rather than the bare scheme because Chrome
   * resolves it natively and follows `S.browser_fallback_url` to the Play Store
   * when the app is missing — no "did it open?" guesswork needed.
   *
   * iOS has no equivalent for custom schemes. The counterpart would be a
   * Universal Link, which needs an `apple-app-site-association` file on this
   * origin plus entitlements inside the app itself.
   */
  buildAppLink({ meetingId, platform }: { meetingId: string; platform: MobilePlatform }): string {
    const path = `meetings/${encodeURIComponent(meetingId)}`;

    if (platform === 'android') {
      const fallback = encodeURIComponent(this.#storeUrls.android);
      return (
        `intent://${path}#Intent;scheme=${this.#scheme};package=${this.#androidPackage};` +
        `S.browser_fallback_url=${fallback};end`
      );
    }

    return `${this.#scheme}://${path}`;
  }

  /**
   * Scoped per meeting so a choice made for one link does not silently skip the
   * screen for the next, and held in `sessionStorage` so it survives the reloads
   * and OAuth round-trips that signing in mid-join causes.
   */
  hasChosenBrowser(meetingId: string): boolean {
    try {
      return sessionStorage.getItem(this.#browserChoiceKey(meetingId)) !== null;
    } catch {
      // Storage can be unavailable (Safari private mode); showing the screen
      // again is the safe way to be wrong.
      return false;
    }
  }

  rememberBrowserChoice(meetingId: string): void {
    try {
      sessionStorage.setItem(this.#browserChoiceKey(meetingId), 'true');
    } catch {
      // Non-fatal: the join itself still proceeds, the screen may just reappear.
    }
  }

  #browserChoiceKey(meetingId: string): string {
    return `${this.#browserChoiceKeyPrefix}${meetingId}`;
  }
}

export const mobileAppRedirectService = new MobileAppRedirectService();
